import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Save,
  Plus,
  X,
  Check,
  Calendar,
  CalendarDays,
  ExternalLink,
  Pencil,
  Trash2,
  Settings2,
  Download,
  Printer,
  CheckSquare,
  StickyNote,
  LayoutGrid,
  List,
  GripVertical,
} from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DossierEditor } from '../components/editor';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { tiptapToMarkdown, downloadMarkdown, printAsPdf } from '../lib/export';
import { ViewGridLayout } from '../components/views/ViewGridLayout';
import { EntityPicker } from '../components/views/EntityPicker';
import {
  parseLayoutConfig,
  generateDefaultLayout,
  reconcileLayout,
  DEFAULT_GROUP_ORDER,
  type LayoutConfig,
  type GridCardConfig,
  type GroupName,
} from '../components/views/layoutUtils';

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface ViewMember {
  entity_type: string;
  entity_id: string;
  name: string;
  sort_order: number;
  include_types?: string[];
}

const ENTITY_TYPE_OPTIONS = [
  { value: 'meeting_instance', label: 'Meetings', icon: Calendar },
  { value: 'task', label: 'Tasks', icon: CheckSquare },
  { value: 'note', label: 'Notes', icon: StickyNote },
] as const;

const MEMBER_TYPE_ICONS: Record<string, typeof Calendar> = {
  meeting_series: Calendar,
  meeting_instance: CalendarDays,
  task: CheckSquare,
  note: StickyNote,
};

interface TagFilterConfig {
  tag_id: string;
  source_types: ('meeting_instance' | 'task' | 'note')[];
}

interface ViewDetail {
  id: string;
  title: string;
  annotations: string | null;
  snapshot_date: string | null;
  entity_types: string;
  layout_config: string | null;
  tag_ids: string[];
  tag_configs: TagFilterConfig[];
  scope_ids: string[];
  tags: Tag[];
  members: ViewMember[];
  created_at: string;
  updated_at: string;
}

interface ViewContentItem {
  source_type: 'meeting_instance' | 'task' | 'note';
  source_id: string;
  title: string;
  description?: string | null;
  tag_name: string;
  tag_id: string;
  tag_color: string;
  // Meeting-specific
  meeting_date?: string;
  series_id?: string;
  series_name?: string;
  tagged_text?: string;
  block_id?: string | null;
  note_tag_id?: string;
  // Task-specific
  status?: string;
  priority?: string;
  // Note-specific
  content?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Annotation: a user-written rich text section pinned between aggregated items
interface Annotation {
  id: string;
  after_item_id: string | null; // null = at the top, or the note_tag_id it comes after
  content: JSONContent;
}

export function ViewDetailPage() {
  const { docId } = useParams<{ docId: string }>();
  const toast = useToast();
  const [view, setView] = useState<ViewDetail | null>(null);
  const [contentItems, setContentItems] = useState<ViewContentItem[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [showEntityPicker, setShowEntityPicker] = useState(false);
  const [members, setMembers] = useState<ViewMember[]>([]);
  const [memberPopover, setMemberPopover] = useState<string | null>(null); // key: `type:id`
  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig | null>(null);
  // Local visibility toggles (what to show in the UI)
  const [visibleTypes, setVisibleTypes] = useState<Set<string>>(new Set(['meeting_instance', 'task', 'note']));

  // Debounced layout save
  const layoutSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isGridMode = layoutConfig?.mode === 'grid';

  // Group order from layout config or default
  const groupOrder = useMemo(() => {
    return layoutConfig?.groupOrder ?? [...DEFAULT_GROUP_ORDER];
  }, [layoutConfig]);

  // Collapsed groups from layout config or default
  const collapsedGroups = useMemo(() => {
    return new Set(layoutConfig?.collapsedGroups ?? []);
  }, [layoutConfig]);

  async function load() {
    if (!docId) return;
    setLoading(true);
    try {
      const [viewData, contentData] = await Promise.all([
        apiGet<ViewDetail>(`/views/${docId}`),
        apiGet<ViewContentItem[]>(`/views/${docId}/content`),
      ]);
      setView(viewData);
      setContentItems(contentData);
      setTitleDraft(viewData.title);
      setMembers(viewData.members ?? []);

      // Parse layout config
      const parsedConfig = parseLayoutConfig(viewData.layout_config);
      setLayoutConfig(parsedConfig);

      // Init visibility from entity_types
      try {
        const types = JSON.parse(viewData.entity_types);
        if (Array.isArray(types)) setVisibleTypes(new Set(types));
      } catch { /* keep defaults */ }

      // Parse existing annotations from JSON
      if (viewData.annotations) {
        try {
          const parsed = JSON.parse(viewData.annotations) as Annotation[];
          setAnnotations(parsed);
        } catch {
          setAnnotations([]);
        }
      } else {
        setAnnotations([]);
      }
    } catch (err) {
      console.error('Failed to load view:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [docId]);

  // Save layout config (debounced)
  const saveLayoutConfig = useCallback((config: LayoutConfig | null) => {
    if (!docId) return;
    if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current);
    layoutSaveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await apiPatch(`/views/${docId}`, {
          layout_config: config ? JSON.stringify(config) : null,
        });
        setLastSaved(new Date().toLocaleTimeString());
      } catch (err) {
        console.error('Failed to save layout:', err);
        toast.error('Failed to save layout');
      } finally {
        setSaving(false);
      }
    }, 500);
  }, [docId, toast]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current);
    };
  }, []);

  const saveAnnotations = useCallback(async (updatedAnnotations: Annotation[]) => {
    if (!docId) return;
    setSaving(true);
    try {
      await apiPatch(`/views/${docId}`, {
        annotations: JSON.stringify(updatedAnnotations),
      });
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save annotations:', err);
      toast.error('Failed to save annotations');
    } finally {
      setSaving(false);
    }
  }, [docId, toast]);

  async function handleTitleSave() {
    if (!docId || !titleDraft.trim()) return;
    setSaving(true);
    try {
      const updated = await apiPatch<ViewDetail>(`/views/${docId}`, {
        title: titleDraft.trim(),
      });
      setView(updated);
      setEditingTitle(false);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save title:', err);
      toast.error('Failed to save title');
    } finally {
      setSaving(false);
    }
  }

  async function handleTagsUpdate(configs: TagFilterConfig[]) {
    if (!docId) return;
    setSaving(true);
    try {
      const updated = await apiPatch<ViewDetail>(`/views/${docId}`, {
        tag_configs: configs,
      });
      setView(updated);
      // Reload content with new tags
      const contentData = await apiGet<ViewContentItem[]>(`/views/${docId}/content`);
      setContentItems(contentData);
      setShowTagEditor(false);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to update tags:', err);
      toast.error('Failed to update tags');
    } finally {
      setSaving(false);
    }
  }

  // Source toggles are now visibility filters — toggle local display, persist preference
  function handleEntityTypeToggle(type: string) {
    setVisibleTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size <= 1) return prev; // Don't allow removing the last type
        next.delete(type);
      } else {
        next.add(type);
      }
      // Persist as entity_types on server
      if (docId) {
        apiPatch(`/views/${docId}`, { entity_types: Array.from(next) }).catch(console.error);
      }
      return next;
    });
  }

  // Members
  function handleMemberAdded(member: ViewMember) {
    setMembers(prev => [...prev, member]);
    // Reload content to include the new member
    if (docId) {
      apiGet<ViewContentItem[]>(`/views/${docId}/content`).then(setContentItems).catch(console.error);
    }
  }

  async function handleRemoveMember(entityType: string, entityId: string) {
    if (!docId) return;
    try {
      await apiDelete(`/views/${docId}/members?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`);
      setMembers(prev => prev.filter(m => !(m.entity_type === entityType && m.entity_id === entityId)));
      // Reload content
      const contentData = await apiGet<ViewContentItem[]>(`/views/${docId}/content`);
      setContentItems(contentData);
    } catch (err) {
      console.error('Failed to remove member:', err);
      toast.error('Failed to remove member');
    }
  }

  async function handleUpdateMemberIncludeTypes(entityType: string, entityId: string, includeTypes: string[]) {
    if (!docId || includeTypes.length === 0) return;
    try {
      const updatedMembers = await apiPatch<ViewMember[]>(`/views/${docId}/members`, {
        entity_type: entityType,
        entity_id: entityId,
        include_types: includeTypes,
      });
      setMembers(updatedMembers);
      // Reload content to reflect the change
      const contentData = await apiGet<ViewContentItem[]>(`/views/${docId}/content`);
      setContentItems(contentData);
    } catch (err) {
      console.error('Failed to update member include types:', err);
      toast.error('Failed to update source types');
    }
  }

  function addAnnotation(afterItemId: string | null) {
    const newAnnotation: Annotation = {
      id: crypto.randomUUID(),
      after_item_id: afterItemId,
      content: { type: 'doc', content: [{ type: 'paragraph' }] },
    };
    const updated = [...annotations, newAnnotation];
    setAnnotations(updated);
    saveAnnotations(updated);
  }

  function updateAnnotation(id: string, content: JSONContent) {
    const updated = annotations.map((a) =>
      a.id === id ? { ...a, content } : a
    );
    setAnnotations(updated);
    saveAnnotations(updated);
  }

  function deleteAnnotation(id: string) {
    const updated = annotations.filter((a) => a.id !== id);
    setAnnotations(updated);
    saveAnnotations(updated);

    // Also remove from layout if in grid mode
    if (layoutConfig && layoutConfig.mode === 'grid') {
      const updatedCards = layoutConfig.cards.filter(c => !(c.type === 'annotation' && c.ref_id === id));
      const newConfig = { ...layoutConfig, cards: updatedCards };
      setLayoutConfig(newConfig);
      saveLayoutConfig(newConfig);
    }
  }

  // Computed data — visibility-filtered
  const meetingItems = useMemo(() =>
    visibleTypes.has('meeting_instance') ? contentItems.filter(i => i.source_type === 'meeting_instance') : [],
    [contentItems, visibleTypes]
  );
  const taskItems = useMemo(() =>
    visibleTypes.has('task') ? contentItems.filter(i => i.source_type === 'task') : [],
    [contentItems, visibleTypes]
  );
  const noteItems = useMemo(() =>
    visibleTypes.has('note') ? contentItems.filter(i => i.source_type === 'note') : [],
    [contentItems, visibleTypes]
  );
  const meetingGroups = useMemo(() => groupByMeeting(meetingItems), [meetingItems]);

  // Reconcile layout when content changes
  useEffect(() => {
    if (!layoutConfig || layoutConfig.mode !== 'grid') return;
    const reconciled = reconcileLayout(
      layoutConfig,
      meetingGroups.map(g => ({ meeting_instance_id: g.meeting_instance_id })),
      taskItems,
      noteItems,
      annotations,
    );
    // Only update if cards changed
    if (JSON.stringify(reconciled.cards) !== JSON.stringify(layoutConfig.cards)) {
      setLayoutConfig(reconciled);
      saveLayoutConfig(reconciled);
    }
  }, [contentItems, annotations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle between list and grid mode
  function handleModeToggle() {
    if (isGridMode) {
      // Switch to list
      const newConfig: LayoutConfig = { ...layoutConfig!, mode: 'list' };
      setLayoutConfig(newConfig);
      saveLayoutConfig(newConfig);
    } else {
      // Switch to grid — generate default layout if none exists, then reconcile
      let newConfig: LayoutConfig;
      if (layoutConfig) {
        newConfig = { ...layoutConfig, mode: 'grid' };
        // Reconcile with current content (items may have changed while in list mode)
        newConfig = reconcileLayout(
          newConfig,
          meetingGroups.map(g => ({ meeting_instance_id: g.meeting_instance_id })),
          taskItems,
          noteItems,
          annotations,
        );
      } else {
        newConfig = generateDefaultLayout(
          meetingGroups.map(g => ({ meeting_instance_id: g.meeting_instance_id })),
          taskItems,
          noteItems,
          annotations,
        );
      }
      setLayoutConfig(newConfig);
      saveLayoutConfig(newConfig);
    }
  }

  // Grid handlers
  function handleGridReorder(cards: GridCardConfig[]) {
    if (!layoutConfig) return;
    const newConfig = { ...layoutConfig, cards };
    setLayoutConfig(newConfig);
    saveLayoutConfig(newConfig);
  }

  function handleGridToggleSpan(cardId: string) {
    if (!layoutConfig) return;
    const newCards = layoutConfig.cards.map(c =>
      c.id === cardId ? { ...c, span: (c.span === 1 ? 2 : 1) as 1 | 2 } : c
    );
    const newConfig = { ...layoutConfig, cards: newCards };
    setLayoutConfig(newConfig);
    saveLayoutConfig(newConfig);
  }

  // List mode: group reorder
  function handleGroupReorder(newOrder: GroupName[]) {
    const newConfig: LayoutConfig = {
      ...(layoutConfig ?? { version: 1, mode: 'list', cards: [] }),
      groupOrder: newOrder,
    };
    setLayoutConfig(newConfig);
    saveLayoutConfig(newConfig);
  }

  // List mode: toggle group collapse
  function handleToggleCollapse(group: string) {
    const current = new Set(layoutConfig?.collapsedGroups ?? []);
    if (current.has(group)) {
      current.delete(group);
    } else {
      current.add(group);
    }
    const newConfig: LayoutConfig = {
      ...(layoutConfig ?? { version: 1, mode: 'list', cards: [] }),
      collapsedGroups: Array.from(current),
    };
    setLayoutConfig(newConfig);
    saveLayoutConfig(newConfig);
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!view) {
    return <div className="h-full overflow-auto p-3 sm:p-6"><div className="text-text-muted text-center py-16">View not found.</div></div>;
  }

  // Build the interleaved view: annotations placed after specific items (list mode only)
  const annotationsByPosition = new Map<string | null, Annotation[]>();
  for (const ann of annotations) {
    const key = ann.after_item_id;
    if (!annotationsByPosition.has(key)) annotationsByPosition.set(key, []);
    annotationsByPosition.get(key)!.push(ann);
  }

  // Build group sections for list mode DnD
  const groupSections: { id: GroupName; label: string; icon: typeof Calendar; content: React.ReactNode }[] = [];

  const meetingsSection = meetingGroups.length > 0 ? (
    <div className="space-y-2">
      {meetingGroups.map((group) => (
        <div key={group.key} className="space-y-2">
          <div className="flex items-center gap-2 pt-2">
            <Calendar size={14} className="text-text-muted" />
            <span className="text-xs font-medium text-text-secondary">
              {formatDate(group.meeting_date)}
            </span>
            <span className="text-xs text-text-muted">&mdash;</span>
            <Link
              to={`/meetings/${group.series_id}/${group.meeting_instance_id}`}
              className="text-xs text-accent hover:text-accent-hover flex items-center gap-1"
            >
              {group.series_name} <ExternalLink size={10} />
            </Link>
          </div>
          {group.items.map((item) => (
            <div key={item.note_tag_id || item.source_id}>
              <MeetingContentCard item={item} />
              {item.note_tag_id && (
                <>
                  <AddAnnotationButton onClick={() => addAnnotation(item.note_tag_id!)} />
                  {(annotationsByPosition.get(item.note_tag_id!) ?? []).map((ann) => (
                    <AnnotationBlock
                      key={ann.id}
                      annotation={ann}
                      onUpdate={updateAnnotation}
                      onDelete={deleteAnnotation}
                    />
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  ) : null;

  const tasksSection = taskItems.length > 0 ? (
    <div className="space-y-2">
      {taskItems.map((item) => (
        <TaskContentCard key={`task-${item.source_id}-${item.tag_id}`} item={item} />
      ))}
    </div>
  ) : null;

  const notesSection = noteItems.length > 0 ? (
    <div className="space-y-2">
      {noteItems.map((item) => (
        <NoteContentCard key={`note-${item.source_id}-${item.tag_id}`} item={item} />
      ))}
    </div>
  ) : null;

  const groupContentMap: Record<GroupName, { label: string; icon: typeof Calendar; content: React.ReactNode | null }> = {
    meetings: { label: 'Meetings', icon: Calendar, content: meetingsSection },
    tasks: { label: 'Tasks', icon: CheckSquare, content: tasksSection },
    notes: { label: 'Notes', icon: StickyNote, content: notesSection },
  };

  for (const g of groupOrder) {
    const gc = groupContentMap[g];
    if (gc.content) {
      groupSections.push({ id: g, label: gc.label, icon: gc.icon, content: gc.content });
    }
  }

  // Add groups not in groupOrder (newly visible)
  for (const g of DEFAULT_GROUP_ORDER) {
    if (!groupOrder.includes(g) && groupContentMap[g].content) {
      const gc = groupContentMap[g];
      groupSections.push({ id: g, label: gc.label, icon: gc.icon, content: gc.content });
    }
  }

  const hasContent = contentItems.length > 0 || annotations.length > 0 || members.length > 0;

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/views"
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronLeft size={16} />
          Views
        </Link>
        <div className="flex items-center gap-2">
          {lastSaved && (
            <span className="flex items-center gap-1 text-xs text-text-muted">
              <Save size={12} />
              Saved at {lastSaved}
            </span>
          )}
          {saving && (
            <span className="text-xs text-accent">Saving...</span>
          )}

          {/* Mode toggle */}
          <div className="flex items-center border border-border rounded-md overflow-hidden no-print">
            <button
              onClick={() => { if (isGridMode) handleModeToggle(); }}
              className={`p-1.5 transition-colors ${
                !isGridMode
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
              }`}
              title="List view"
            >
              <List size={14} />
            </button>
            <button
              onClick={() => { if (!isGridMode) handleModeToggle(); }}
              className={`p-1.5 transition-colors ${
                isGridMode
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
              }`}
              title="Grid view"
            >
              <LayoutGrid size={14} />
            </button>
          </div>

          <button
            onClick={() => {
              let md = `# ${view!.title}\n\n`;
              for (const ann of annotations) {
                md += tiptapToMarkdown(ann.content) + '\n';
              }
              for (const item of meetingItems) {
                md += `- ${item.tagged_text} [${item.tag_name}]\n`;
              }
              for (const item of taskItems) {
                md += `- [${item.status}] ${item.title} [${item.tag_name}]\n`;
              }
              for (const item of noteItems) {
                md += `- ${item.title} [${item.tag_name}]\n`;
              }
              downloadMarkdown(md, view!.title);
            }}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors no-print"
            title="Export as Markdown"
          >
            <Download size={14} />
          </button>
          <button
            onClick={printAsPdf}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors no-print"
            title="Print / Save as PDF"
          >
            <Printer size={14} />
          </button>
        </div>
      </div>

      {/* Header: editable title */}
      <div className="mb-4">
        {editingTitle ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSave();
                if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(view.title); }
              }}
              autoFocus
              className="text-2xl font-bold bg-bg-input border border-border rounded-md px-2 py-1 text-text-primary focus:outline-none focus:border-accent flex-1"
            />
            <button onClick={handleTitleSave} className="p-1 text-success hover:text-success/80">
              <Check size={20} />
            </button>
            <button onClick={() => { setEditingTitle(false); setTitleDraft(view.title); }} className="p-1 text-text-muted hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            <h1 className="text-2xl font-bold text-text-primary">{view.title}</h1>
            <button
              onClick={() => setEditingTitle(true)}
              className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-text-primary transition-opacity"
            >
              <Pencil size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Entity type toggles (visibility filters) */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-text-muted uppercase tracking-wider">Show:</span>
        {ENTITY_TYPE_OPTIONS.map((opt) => {
          const active = visibleTypes.has(opt.value);
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              onClick={() => handleEntityTypeToggle(opt.value)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                active
                  ? 'bg-accent/10 border-accent text-accent'
                  : 'border-border text-text-muted hover:text-text-secondary hover:border-border-hover'
              }`}
            >
              <Icon size={12} />
              {opt.label} {active ? '\u2713' : ''}
            </button>
          );
        })}
      </div>

      {/* Tag filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-text-muted uppercase tracking-wider">Filters:</span>
        {view.tags.map((tag) => {
          const config = view.tag_configs?.find(tc => tc.tag_id === tag.id);
          const sourceTypes = config?.source_types ?? ['meeting_instance', 'task', 'note'];
          return (
            <button
              key={tag.id}
              onClick={() => setShowTagEditor(true)}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full cursor-pointer hover:ring-1 hover:ring-current transition-all"
              style={{ backgroundColor: tag.color + '20', color: tag.color }}
              title="Click to edit tag filters & source types"
            >
              {tag.name}
              <span className="flex items-center gap-0.5 ml-0.5 opacity-70">
                {sourceTypes.includes('meeting_instance') && <Calendar size={9} />}
                {sourceTypes.includes('task') && <CheckSquare size={9} />}
                {sourceTypes.includes('note') && <StickyNote size={9} />}
              </span>
            </button>
          );
        })}
        {view.tags.length === 0 && (
          <span className="text-xs text-text-muted italic">No tag filters</span>
        )}
        <button
          onClick={() => setShowTagEditor(true)}
          className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover px-2.5 py-1 rounded-md border border-accent/30 hover:border-accent hover:bg-accent/10 transition-colors"
        >
          <Settings2 size={12} /> Edit Tags
        </button>
      </div>

      {/* Direct members */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <span className="text-xs text-text-muted uppercase tracking-wider">Content:</span>
        {members.map((m) => {
          const Icon = MEMBER_TYPE_ICONS[m.entity_type] ?? CheckSquare;
          const isMeetingType = m.entity_type === 'meeting_series' || m.entity_type === 'meeting_instance';
          const memberKey = `${m.entity_type}:${m.entity_id}`;
          const isPopoverOpen = memberPopover === memberKey;
          const memberIncludeTypes = m.include_types ?? ['meeting_instance', 'task', 'note'];

          return (
            <span
              key={memberKey}
              className="relative flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-bg-card border border-border text-text-secondary"
            >
              <Icon size={10} />
              {isMeetingType ? (
                <button
                  onClick={() => setMemberPopover(isPopoverOpen ? null : memberKey)}
                  className="flex items-center gap-1 hover:text-text-primary transition-colors"
                >
                  <span className="max-w-32 truncate">{m.name}</span>
                  <span className="flex items-center gap-0.5 opacity-70">
                    {memberIncludeTypes.includes('meeting_instance') && <CalendarDays size={9} />}
                    {memberIncludeTypes.includes('task') && <CheckSquare size={9} />}
                    {memberIncludeTypes.includes('note') && <StickyNote size={9} />}
                  </span>
                </button>
              ) : (
                <span className="max-w-32 truncate">{m.name}</span>
              )}
              <button
                onClick={() => handleRemoveMember(m.entity_type, m.entity_id)}
                className="text-text-muted hover:text-danger ml-0.5"
              >
                <X size={10} />
              </button>
              {/* Inline popover for meeting-type members */}
              {isPopoverOpen && isMeetingType && (
                <>
                  <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setMemberPopover(null); }} />
                  <div
                    className="absolute top-full left-0 mt-1 z-50 bg-bg-surface border border-border rounded-lg shadow-xl p-2 min-w-[160px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="text-[10px] text-text-muted uppercase tracking-wider px-1 mb-1">Source types</div>
                    {ENTITY_TYPE_OPTIONS.map(opt => {
                      const active = memberIncludeTypes.includes(opt.value);
                      const TypeIcon = opt.icon;
                      return (
                        <button
                          key={opt.value}
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = active
                              ? memberIncludeTypes.filter(t => t !== opt.value)
                              : [...memberIncludeTypes, opt.value];
                            if (next.length === 0) return; // Must keep at least one
                            handleUpdateMemberIncludeTypes(m.entity_type, m.entity_id, next);
                          }}
                          className={`flex items-center gap-2 w-full px-2 py-1 rounded-md text-xs transition-colors ${
                            active ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-secondary hover:bg-bg-hover'
                          }`}
                        >
                          <TypeIcon size={12} />
                          <span className="flex-1">{opt.label}</span>
                          {active && <Check size={12} />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </span>
          );
        })}
        <button
          onClick={() => setShowEntityPicker(true)}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-accent px-2 py-0.5 rounded-md hover:bg-bg-hover transition-colors"
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {/* Content area */}
      {!hasContent ? (
        <div className="text-center py-12 text-text-muted">
          <p className="text-sm">
            {view.tags.length === 0 && members.length === 0
              ? 'Add tag filters or content items to start aggregating.'
              : 'No content found matching your filters.'}
          </p>
        </div>
      ) : isGridMode && layoutConfig ? (
        /* Grid mode */
        <ViewGridLayout
          cards={layoutConfig.cards}
          meetingGroups={meetingGroups}
          taskItems={taskItems}
          noteItems={noteItems}
          annotations={annotations}
          onReorder={handleGridReorder}
          onToggleSpan={handleGridToggleSpan}
          onUpdateAnnotation={updateAnnotation}
          onDeleteAnnotation={deleteAnnotation}
        />
      ) : (
        /* List mode with DnD groups */
        <div className="space-y-3">
          {/* Annotations at the top (before any aggregated content) */}
          <AddAnnotationButton onClick={() => addAnnotation(null)} label="Add note at top" />
          {(annotationsByPosition.get(null) ?? []).map((ann) => (
            <AnnotationBlock
              key={ann.id}
              annotation={ann}
              onUpdate={updateAnnotation}
              onDelete={deleteAnnotation}
            />
          ))}

          {/* Sortable group sections */}
          <SortableGroupList
            groups={groupSections}
            collapsedGroups={collapsedGroups}
            onReorder={(newOrder) => handleGroupReorder(newOrder)}
            onToggleCollapse={handleToggleCollapse}
          />
        </div>
      )}

      {showTagEditor && (
        <TagEditorModal
          currentTagConfigs={view.tag_configs ?? view.tag_ids.map(id => ({ tag_id: id, source_types: ['meeting_instance', 'task', 'note'] as ('meeting_instance' | 'task' | 'note')[] }))}
          onSave={handleTagsUpdate}
          onClose={() => setShowTagEditor(false)}
        />
      )}

      {showEntityPicker && docId && (
        <EntityPicker
          viewId={docId}
          existingMembers={members}
          onAdd={handleMemberAdded}
          onClose={() => setShowEntityPicker(false)}
        />
      )}
    </div>
    </div>
  );
}

// -- Sortable Group List --

interface GroupSection {
  id: GroupName;
  label: string;
  icon: typeof Calendar;
  content: React.ReactNode;
}

function SortableGroupList({
  groups,
  collapsedGroups,
  onReorder,
  onToggleCollapse,
}: {
  groups: GroupSection[];
  collapsedGroups: Set<string>;
  onReorder: (newOrder: GroupName[]) => void;
  onToggleCollapse: (group: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = groups.findIndex(g => g.id === active.id);
    const newIndex = groups.findIndex(g => g.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(groups, oldIndex, newIndex);
    onReorder(reordered.map(g => g.id));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={groups.map(g => g.id)} strategy={verticalListSortingStrategy}>
        {groups.map(group => (
          <SortableGroup
            key={group.id}
            group={group}
            isCollapsed={collapsedGroups.has(group.id)}
            onToggleCollapse={() => onToggleCollapse(group.id)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableGroup({
  group,
  isCollapsed,
  onToggleCollapse,
}: {
  group: GroupSection;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = group.icon;

  return (
    <div ref={setNodeRef} style={style} className="group/section">
      <div className="flex items-center gap-2 pt-3 pb-1">
        <button
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing p-0.5 text-text-muted hover:text-text-secondary transition-colors opacity-0 group-hover/section:opacity-100 flex-shrink-0"
          title="Drag to reorder"
        >
          <GripVertical size={12} />
        </button>
        <button onClick={onToggleCollapse} className="text-text-muted hover:text-text-secondary">
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </button>
        <Icon size={14} className="text-text-muted" />
        <span className="text-xs font-medium text-text-secondary uppercase tracking-wider">{group.label}</span>
      </div>
      {!isCollapsed && group.content}
    </div>
  );
}

// -- Subcomponents --

function MeetingContentCard({ item }: { item: ViewContentItem }) {
  return (
    <div className="bg-bg-card border border-border rounded-lg px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-text-primary whitespace-pre-wrap">{item.tagged_text || item.title}</p>
        </div>
        {item.tag_name && (
          <span
            className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
          >
            {item.tag_name}
          </span>
        )}
      </div>
    </div>
  );
}

function TaskContentCard({ item }: { item: ViewContentItem }) {
  const statusColors: Record<string, string> = {
    todo: '#64748b',
    in_progress: '#f59e0b',
    done: '#22c55e',
    blocked: '#ef4444',
  };
  const statusColor = statusColors[item.status ?? ''] ?? '#64748b';

  return (
    <Link
      to={`/tasks/${item.source_id}`}
      className="block bg-bg-card border border-border rounded-lg px-4 py-3 hover:border-border-hover transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[10px] px-1.5 py-0.5 rounded uppercase font-medium"
              style={{ backgroundColor: statusColor + '20', color: statusColor }}
            >
              {(item.status ?? 'todo').replace('_', ' ')}
            </span>
            <span className="text-sm font-medium text-text-primary truncate">{item.title}</span>
          </div>
          {item.description && (
            <p className="text-xs text-text-muted line-clamp-2">{item.description}</p>
          )}
        </div>
        {item.tag_name && (
          <span
            className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
          >
            {item.tag_name}
          </span>
        )}
      </div>
    </Link>
  );
}

function NoteContentCard({ item }: { item: ViewContentItem }) {
  return (
    <Link
      to={`/notes/${item.source_id}`}
      className="block bg-bg-card border border-border rounded-lg px-4 py-3 hover:border-border-hover transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-text-primary truncate">{item.title}</span>
          {item.updated_at && (
            <p className="text-xs text-text-muted mt-1">
              Updated {formatDate(item.updated_at)}
            </p>
          )}
        </div>
        {item.tag_name && (
          <span
            className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
          >
            {item.tag_name}
          </span>
        )}
      </div>
    </Link>
  );
}

function AnnotationBlock({
  annotation,
  onUpdate,
  onDelete,
}: {
  annotation: Annotation;
  onUpdate: (id: string, content: JSONContent) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="relative group">
      <div className="view-annotation-editor border-l-2 border-accent/30 pl-3 ml-2">
        <DossierEditor
          content={annotation.content}
          onUpdate={(content) => onUpdate(annotation.id, content)}
          editable={true}
          placeholder="Write a note..."
        />
      </div>
      <button
        onClick={() => onDelete(annotation.id)}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-bg-surface border border-border rounded text-text-muted hover:text-danger transition-all"
        title="Remove annotation"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function AddAnnotationButton({ onClick, label }: { onClick: () => void; label?: string }) {
  return (
    <div className="flex justify-center py-1">
      <button
        onClick={onClick}
        className="flex items-center gap-1 text-xs text-text-muted hover:text-accent px-2 py-0.5 rounded hover:bg-bg-hover transition-colors"
      >
        <Plus size={12} /> {label ?? 'Add note'}
      </button>
    </div>
  );
}

type SourceType = 'meeting_instance' | 'task' | 'note';
const ALL_SOURCE_TYPES: SourceType[] = ['meeting_instance', 'task', 'note'];
const SOURCE_TYPE_ICONS: Record<SourceType, typeof Calendar> = {
  meeting_instance: Calendar,
  task: CheckSquare,
  note: StickyNote,
};
const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  meeting_instance: 'Meetings',
  task: 'Tasks',
  note: 'Notes',
};

function TagEditorModal({
  currentTagConfigs,
  onSave,
  onClose,
}: {
  currentTagConfigs: TagFilterConfig[];
  onSave: (configs: TagFilterConfig[]) => void;
  onClose: () => void;
}) {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  // Map from tag_id → Set of selected source_types
  const [tagConfigMap, setTagConfigMap] = useState<Map<string, Set<SourceType>>>(() => {
    const m = new Map<string, Set<SourceType>>();
    for (const tc of currentTagConfigs) {
      m.set(tc.tag_id, new Set(tc.source_types));
    }
    return m;
  });
  const [loadingTags, setLoadingTags] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    apiGet<Tag[]>('/tags')
      .then(setAllTags)
      .catch(console.error)
      .finally(() => setLoadingTags(false));
  }, []);

  function toggleTag(tagId: string) {
    setTagConfigMap(prev => {
      const next = new Map(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.set(tagId, new Set(ALL_SOURCE_TYPES));
      }
      return next;
    });
  }

  function toggleSourceType(tagId: string, st: SourceType) {
    setTagConfigMap(prev => {
      const next = new Map(prev);
      const current = next.get(tagId);
      if (!current) return prev;
      const updated = new Set(current);
      if (updated.has(st)) {
        if (updated.size <= 1) return prev; // keep at least one
        updated.delete(st);
      } else {
        updated.add(st);
      }
      next.set(tagId, updated);
      return next;
    });
  }

  const filteredTags = searchQuery.trim()
    ? allTags.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : allTags;

  const hasExactMatch = allTags.some(t => t.name.toLowerCase() === searchQuery.trim().toLowerCase());

  async function handleCreateTag() {
    if (!searchQuery.trim() || creating) return;
    setCreating(true);
    try {
      const newTag = await apiPost<Tag>('/tags', { name: searchQuery.trim() });
      setAllTags(prev => [...prev, newTag]);
      setTagConfigMap(prev => {
        const next = new Map(prev);
        next.set(newTag.id, new Set(ALL_SOURCE_TYPES));
        return next;
      });
      setSearchQuery('');
    } catch (err) {
      console.error('Failed to create tag:', err);
    } finally {
      setCreating(false);
    }
  }

  function handleSave() {
    const configs: TagFilterConfig[] = [];
    for (const [tagId, sourceTypes] of tagConfigMap) {
      configs.push({ tag_id: tagId, source_types: Array.from(sourceTypes) });
    }
    onSave(configs);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">Edit Tag Filters</h2>
        <p className="text-xs text-text-muted mb-3">
          Select tags and choose which content types each tag pulls in.
        </p>

        {/* Search input */}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search or create tags..."
          className="w-full px-3 py-1.5 mb-2 text-sm bg-bg-input border border-border rounded-md text-text-primary placeholder-text-muted focus:outline-none focus:border-accent"
        />

        {loadingTags ? (
          <div className="text-xs text-text-muted">Loading tags...</div>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
            {filteredTags.length === 0 && !searchQuery.trim() && (
              <div className="text-xs text-text-muted">No tags available.</div>
            )}
            {filteredTags.map((tag) => {
              const isSelected = tagConfigMap.has(tag.id);
              const sourceTypes = tagConfigMap.get(tag.id);
              return (
                <div key={tag.id} className={`rounded-md transition-colors ${isSelected ? 'bg-accent/10' : ''}`}>
                  <button
                    onClick={() => toggleTag(tag.id)}
                    className={`flex items-center gap-2 w-full px-3 py-1.5 text-left text-sm ${
                      isSelected ? 'text-text-primary' : 'text-text-secondary hover:bg-bg-hover'
                    }`}
                  >
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="flex-1">{tag.name}</span>
                    {isSelected && <Check size={14} className="text-accent" />}
                  </button>
                  {/* Source type toggles for selected tags */}
                  {isSelected && sourceTypes && (
                    <div className="flex items-center gap-1.5 px-3 pb-2 ml-5">
                      <span className="text-[11px] text-text-muted mr-0.5">Include:</span>
                      {ALL_SOURCE_TYPES.map(st => {
                        const Icon = SOURCE_TYPE_ICONS[st];
                        const active = sourceTypes.has(st);
                        return (
                          <button
                            key={st}
                            onClick={() => toggleSourceType(tag.id, st)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs border transition-colors ${
                              active
                                ? 'bg-accent/15 border-accent/40 text-accent'
                                : 'bg-bg-input border-border text-text-muted hover:text-text-secondary hover:border-border-hover'
                            }`}
                            title={SOURCE_TYPE_LABELS[st]}
                          >
                            <Icon size={12} />
                            <span>{SOURCE_TYPE_LABELS[st]}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Inline create option */}
            {searchQuery.trim() && !hasExactMatch && (
              <button
                onClick={handleCreateTag}
                disabled={creating}
                className="flex items-center gap-2 w-full px-3 py-1.5 rounded-md text-left text-sm text-accent hover:bg-bg-hover transition-colors"
              >
                <Plus size={14} />
                <span>Create &ldquo;{searchQuery.trim()}&rdquo;</span>
              </button>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// -- Helpers --

interface MeetingGroup {
  key: string;
  meeting_instance_id: string;
  meeting_date: string;
  series_name: string;
  series_id: string;
  items: ViewContentItem[];
}

function groupByMeeting(items: ViewContentItem[]): MeetingGroup[] {
  const groups = new Map<string, MeetingGroup>();
  for (const item of items) {
    const key = item.source_id;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        meeting_instance_id: item.source_id,
        meeting_date: item.meeting_date ?? '',
        series_name: item.series_name ?? '',
        series_id: item.series_id ?? '',
        items: [],
      });
    }
    groups.get(key)!.items.push(item);
  }
  return Array.from(groups.values());
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
